import React, { useContext, useEffect, useState } from 'react';
import { find, findLast } from 'lodash';
import { useHistory } from 'react-router-dom';

import css from './KBAQuestions.module.scss';
import Button from '../../components/UI/Button';
import RadioButton from '../../components/UI/RadioButtonGroup/RadioButton';
import { KYC_ERROR_CODES } from '../WidgetPage/constants';
import Message from '../../components/UI/Message';
import { useAppDispatchers, useApp } from '../../state/app';
import { AnalyticsContext } from '../../ModularReduxAnalytics';

interface KBAQuestionsProps {
  ssnLast4: string;
}

const KBAQuestions = ({ ssnLast4 }: KBAQuestionsProps) => {
  const {
    unresolvedErrors,
    error,
    message,
    loading,
    registrationToken,
    groupId,
    flowId,
    extras
  } = useApp();
  const { answer, resolveError } = useAppDispatchers();
  const { analytics } = useContext(AnalyticsContext);
  const history = useHistory();

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showChallengeQuestion, setShowChallengeQuestion] = useState(false);
  const [answers, setAnswers] = useState<string[]>([]);

  const getKYCResponse = () => {
    // If there are challenge questions, these will serve as the response.
    const challengeQuestion = find(
      unresolvedErrors,
      err => err.code === KYC_ERROR_CODES.challenge
    );
    // If there are multiple partial fails, pick the last one
    const lastPartialFailure = findLast(
      unresolvedErrors,
      err => err.code === KYC_ERROR_CODES.partial
    );

    if (challengeQuestion || lastPartialFailure) {
      return challengeQuestion
        ? challengeQuestion.meta
        : lastPartialFailure.meta;
    }
    return null;
  };
  const kycResponse = getKYCResponse();

  const numberOfQuestions =
    kycResponse && kycResponse.questionsList
      ? kycResponse.questionsList.length
      : 0;
  const header = showChallengeQuestion
    ? 'Based on your answers we haven’t yet been able to verify your identity. We have one final question to ask you.'
    : 'Thank you. We’re almost done, we just have a few more questions we need to ask';
  const subHeader = showChallengeQuestion
    ? 'FINAL QUESTION'
    : `QUESTION ${currentQuestion + 1} of ${numberOfQuestions}`;

  const handleRadioChange = e => {
    const updatedAnswers = answers.slice(0);
    updatedAnswers[currentQuestion] = e.target.value;
    setAnswers(updatedAnswers);
  };

  const goBack = () => {
    if (currentQuestion !== 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const resolveKYCErrors = () => {
    const allCodes = Object.values(KYC_ERROR_CODES);
    unresolvedErrors
      .filter(err => allCodes.indexOf(err.code) !== -1)
      .forEach(err => {
        resolveError({ errorId: err.id });
      });
    analytics.trackEvent('Registration Widget Corrected', {
      eventProps: {
        widgets_submitted: 'set_kyc_questions',
        group_id: groupId,
        flow_id: flowId,
        error_code: 'ERR_KYC_PARTIAL_FAIL',
        error_action: 'verify_identity'
      }
    });
  };

  const submitKBAAnswers = () => {
    if (currentQuestion + 1 !== kycResponse.questionsList.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const kycAnswers: { type: string; answer: string }[] = [];
      for (let i = 0; i < answers.length; i += 1) {
        kycAnswers.push({
          type: kycResponse.questionsList[i].type,
          answer: answers[i]
        });
      }

      const question = showChallengeQuestion
        ? 'submit_kyc_challenge'
        : 'submit_kyc_questions';

      answer({
        accessToken: registrationToken,
        questions: [question],
        answers: [
          {
            idNumber: parseFloat(kycResponse.idNumber),
            answers: kycAnswers,
            ssnLast4
          }
        ]
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const hasPassed = unresolvedErrors.some(
      err => err.code === KYC_ERROR_CODES.pass
    );
    if (hasPassed) {
      resolveKYCErrors();
    }
  }, []);

  useEffect(() => {
    const hasPassed = unresolvedErrors.some(
      err => err.code === KYC_ERROR_CODES.pass
    );
    const hasChallengeQuestion = unresolvedErrors.some(
      err => err.code === KYC_ERROR_CODES.challenge
    );
    const hasFullFail = unresolvedErrors.some(
      err => err.code === KYC_ERROR_CODES.fail
    );
    if (hasFullFail) {
      history.replace('/verify-declined');
    } else if (hasPassed) {
      resolveKYCErrors();
    } else if (hasChallengeQuestion) {
      setShowChallengeQuestion(true);
      setCurrentQuestion(0);
      setAnswers([]);
    }
  }, [unresolvedErrors]);

  useEffect(() => {
    if (extras) {
      // payload changes for subsequent responses
      if (extras.status === 'partial') {
        setShowChallengeQuestion(true);
        setCurrentQuestion(0);
        setAnswers([]);
      }
      if (extras.status === 'fail') {
        history.replace('/verify-declined');
      }
    }
  }, [extras]);

  if (numberOfQuestions !== 0) {
    return (
      <div data-e2e="kyc">
        <h1 data-e2e="kyc-header">Let&apos;s verify your identity.</h1>
        <h2 data-e2e="kyc-subheader">{header}</h2>
        <Message error={error} text={message} />
        <div className={css.KYCQuestion} data-e2e="question-form">
          <p>{subHeader}</p>
          <label data-e2e={`question-${currentQuestion + 1}`}>
            {kycResponse.questionsList &&
            kycResponse.questionsList[currentQuestion]
              ? kycResponse.questionsList[currentQuestion].prompt
              : ''}
          </label>
          {kycResponse.questionsList[currentQuestion]?.answersList.map(
            (answer, i) => (
              <div className={`${css.answerSelect} fs-mask`} key={i}>
                <RadioButton
                  id={i}
                  key={i}
                  value={answer}
                  label={answer}
                  selected={answers[currentQuestion] === answer}
                  onChange={handleRadioChange}
                  data-e2e={`answer-${i + 1}`}
                />
              </div>
            )
          )}

          {showChallengeQuestion ? (
            <div style={{ margin: '20px auto' }}>
              <Button
                text="Next"
                loading={loading}
                disabled={!answers[currentQuestion]}
                onClick={submitKBAAnswers}
                data-e2e="next"
              />
            </div>
          ) : (
            <div className={css.buttonsContainer}>
              <Button
                text="Back"
                loading={loading}
                disabled={currentQuestion === 0}
                secondary={true}
                onClick={goBack}
                data-e2e="back"
              />
              <Button
                text="Next"
                loading={loading}
                disabled={!answers[currentQuestion]}
                onClick={submitKBAAnswers}
                data-e2e="next"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
  return <div />;
};

export default KBAQuestions;
